/**
 * CsvImport — batch-import modal for whitelist rosters (L3 SN / L4 model).
 *
 * Flow: pick a .csv → parse COLUMN 0 ONLY (operator sheets carry extra columns
 * we must ignore) → preview what will import vs what was skipped (header rows /
 * CJK / malformed values fail validation and are shown, never silently dropped)
 * → the operator types the row COUNT to confirm → sequential loop over the
 * AUDITED single-write endpoint (v0.45.5 bulk pattern: no new batch endpoint,
 * every row writes its own audit record) with live progress → summary with
 * per-row failures.
 */
import { useRef, useState } from 'react';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { TextInput } from '@/components/Field';
import { parseCsvFirstColumn } from '@/lib/csv';

/** Importable value: printable ASCII, no spaces, sane length. A header cell
 *  like 「設備或生財器具名稱」 fails this and lands in the skipped list. */
const VALID_RE = /^[\x21-\x7e]{1,64}$/;

interface Parsed {
  values: string[];   // normalized, deduped, validated — will import
  skipped: string[];  // raw values that failed validation (headers / junk)
  dupes: number;      // duplicates removed inside the file
}

function parseFile(text: string): Parsed {
  const raw = parseCsvFirstColumn(text);
  const seen = new Set<string>();
  const values: string[] = [];
  const skipped: string[] = [];
  let dupes = 0;
  for (const r of raw) {
    const v = r.trim().toUpperCase();
    if (v === '') continue; // blank line — not worth reporting
    if (!VALID_RE.test(v)) { skipped.push(r.trim()); continue; }
    if (seen.has(v)) { dupes++; continue; }
    seen.add(v);
    values.push(v);
  }
  return { values, skipped, dupes };
}

type Phase = 'pick' | 'preview' | 'running' | 'done';

interface Failure { value: string; error: string }

export function CsvImport({
  open, title, itemLabel, hint, importOne, onClose, onImported,
}: {
  open: boolean;
  title: string;
  /** e.g. 「序號」 / 「機型碼」 — used in copy. */
  itemLabel: string;
  /** Shown under the file picker (what column 0 must contain). */
  hint: string;
  /** The audited single-write call for one normalized value. */
  importOne: (value: string) => Promise<unknown>;
  onClose: () => void;
  /** Called after a run that imported ≥1 row (parent refreshes its list). */
  onImported: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>('pick');
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState<Parsed | null>(null);
  const [parseErr, setParseErr] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const [progress, setProgress] = useState(0);
  const [okCount, setOkCount] = useState(0);
  const [failures, setFailures] = useState<Failure[]>([]);

  const reset = () => {
    setPhase('pick'); setFileName(''); setParsed(null); setParseErr(null);
    setTyped(''); setProgress(0); setOkCount(0); setFailures([]);
    if (fileRef.current) fileRef.current.value = '';
  };
  const close = () => {
    if (phase === 'running') return; // never abandon a half-run silently
    reset(); onClose();
  };

  const onPick = async (f: File | undefined) => {
    if (!f) return;
    setParseErr(null);
    try {
      const p = parseFile(await f.text());
      if (p.values.length === 0) {
        setParseErr('第一欄沒有可匯入的值(僅接受不含空白的 ASCII 代碼;標題列會自動略過)。');
        return;
      }
      setFileName(f.name);
      setParsed(p);
      setTyped('');
      setPhase('preview');
    } catch (e) {
      setParseErr(e instanceof Error ? e.message : String(e));
    }
  };

  const expect = String(parsed?.values.length ?? 0);
  const matched = typed.trim() === expect;

  const run = async () => {
    if (!parsed || !matched) return;
    setPhase('running');
    let ok = 0;
    const fails: Failure[] = [];
    for (let i = 0; i < parsed.values.length; i++) {
      const v = parsed.values[i];
      try {
        await importOne(v);
        ok++;
      } catch (e) {
        fails.push({ value: v, error: e instanceof Error ? e.message : String(e) });
      }
      setProgress(i + 1);
    }
    setOkCount(ok);
    setFailures(fails);
    setPhase('done');
    if (ok > 0) onImported();
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={title}
      footer={
        phase === 'preview' ? (
          <>
            <Button onClick={() => { reset(); }}>重選檔案</Button>
            <Button variant="primary" disabled={!matched} onClick={() => void run()}>
              匯入 {expect} 筆
            </Button>
          </>
        ) : phase === 'running' ? (
          <Button loading disabled>匯入中…</Button>
        ) : phase === 'done' ? (
          <Button variant="primary" onClick={close}>完成</Button>
        ) : (
          <Button onClick={close}>取消</Button>
        )
      }
    >
      <div className="stack" style={{ gap: 'var(--space-4)' }}>
        {phase === 'pick' && (
          <>
            <p className="subtle" style={{ margin: 0 }}>
              選擇 CSV 檔 — <b>只讀取第一欄</b>,其餘欄位忽略。{hint}
            </p>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              aria-label="選擇 CSV 檔案"
              onChange={(e) => void onPick(e.target.files?.[0])}
            />
            {parseErr && <div className="field-msg field-msg-error" role="alert">{parseErr}</div>}
          </>
        )}

        {phase === 'preview' && parsed && (
          <>
            <p className="subtle" style={{ margin: 0 }}>
              <b>{fileName}</b> — 將匯入 <b>{parsed.values.length}</b> 筆{itemLabel}
              {parsed.dupes > 0 && <>(檔內重複 {parsed.dupes} 筆已合併)</>}
              {parsed.skipped.length > 0 && <>;略過 {parsed.skipped.length} 筆非{itemLabel}內容</>}
              。
            </p>
            <div className="csv-preview mono" role="list" aria-label={`將匯入的${itemLabel}`}>
              {parsed.values.map((v) => <div key={v} role="listitem">{v}</div>)}
            </div>
            {parsed.skipped.length > 0 && (
              <details>
                <summary className="subtle">已略過的 {parsed.skipped.length} 筆(標題列 / 格式不符)</summary>
                <div className="csv-preview mono">
                  {parsed.skipped.map((v, i) => <div key={i}>{v || '(空白)'}</div>)}
                </div>
              </details>
            )}
            <TextInput
              label={`輸入筆數 ${expect} 以確認匯入`}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={expect}
              autoComplete="off"
              spellCheck={false}
            />
          </>
        )}

        {phase === 'running' && parsed && (
          <p className="subtle" style={{ margin: 0 }} role="status">
            匯入中… {progress} / {parsed.values.length}(每筆各自寫入稽核)
          </p>
        )}

        {phase === 'done' && (
          <>
            <p style={{ margin: 0 }} role="status">
              匯入完成:成功 <b>{okCount}</b> 筆
              {failures.length > 0 && <>、失敗 <b>{failures.length}</b> 筆</>}。
            </p>
            {failures.length > 0 && (
              <div className="csv-preview mono" aria-label="失敗清單">
                {failures.map((f) => (
                  <div key={f.value}>{f.value} — {f.error}</div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
